import { categoryMeta } from "@passfirst/types/vertical-config";

import type { CredentialRow } from "./use-credentials";

export interface CredentialGroup {
  category: string;
  label: string;
  order: number;
  rows: CredentialRow[];
}

// Normalize a string for accent-insensitive, case-insensitive substring
// matching. Keeps the filter forgiving without pulling in a fuzzy-search dep.
function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Does a credential match the search query? Matches across the full name,
// short name, scope-hint meta, and the industry-category label so a search
// like "maritime" surfaces every credential in that group.
function matches(row: CredentialRow, q: string): boolean {
  if (!q) return true;
  const haystack = norm(
    [row.name, row.shortName, row.meta, categoryMeta(row.category).label].join(
      " ",
    ),
  );
  // AND across whitespace-separated terms so "faa drone" narrows.
  return norm(q)
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

// Filter by query, then group by category and order both the groups (by
// CATEGORIES order) and the rows within each group (alphabetical by name).
// Vertical-agnostic: category labels/order come from the shared CATEGORIES
// table, never hard-coded here.
export function groupCredentials(
  rows: CredentialRow[],
  query: string,
): CredentialGroup[] {
  const byCategory = new Map<string, CredentialRow[]>();
  for (const row of rows) {
    if (!matches(row, query)) continue;
    const bucket = byCategory.get(row.category);
    if (bucket) bucket.push(row);
    else byCategory.set(row.category, [row]);
  }

  const groups: CredentialGroup[] = [];
  for (const [category, groupRows] of byCategory) {
    const { label, order } = categoryMeta(category);
    groupRows.sort((a, b) => a.name.localeCompare(b.name));
    groups.push({ category, label, order, rows: groupRows });
  }
  groups.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  return groups;
}
